// @ts-nocheck

import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BoxV2, ButtonUI, LoadingSkeleton, TextArea, useDidUpdate, useIsMounted } from 'sonata-react-components';

import { ReportingCommentDeletionPrompt } from '@/modules/reporting/ReportingCommentSection/ReportingCommentDeletionPrompt';
import { createComment, deleteComment, getComments, updateComment } from '@/modules/reporting/reporting.resources';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { ReportingCommentSectionStyle } from './reportingCommentSection.style';

function ReportingCommentSection(props) {
  const COMMENT_MAX_LENGTH = 500;
  const { hide = false, onCancel, onChange, onlyView = false, section } = props;
  const { formatMessage } = useIntl();
  const isMounted = useIsMounted();
  const token = {};

  const cids = useSelector(getCampaigns);

  const [{ commentData, commentIdToDelete, editMode, loading, loadingButton, showPrompt, unsavedComment }, setState] =
    useState({
      commentData: null,
      commentIdToDelete: undefined,
      editMode: !onlyView,
      loading: false,
      loadingButton: false,
      showPrompt: false,
      unsavedComment: undefined
    });

  const catchFunction = (customState = {}) => {
    if (!isMounted()) return;
    setState(prevState => ({
      ...prevState,
      loading: false,
      ...customState
    }));
  };

  const loadComments = () => {
    setState(prevState => ({
      ...prevState,
      loading: true
    }));
    getComments(cids, section, token)
      .then(comments => {
        onChange(comments[0]);
        setState(prevState => ({
          ...prevState,
          commentData: comments[0],
          editMode: !comments[0],
          loading: false
        }));
      })
      .catch(catchFunction({ commentData: null }));
  };

  const performUpdateComment = (commentId, comment) => {
    setState(prevState => ({
      ...prevState,
      loadingButton: true
    }));
    updateComment(commentId, comment, token)
      .then(updatedComment => {
        setState(prevState => ({
          ...prevState,
          commentData: updatedComment,
          editMode: false,
          loadingButton: false
        }));
        onChange(updatedComment);
      })
      .catch(catchFunction);
  };

  const performCreateComment = comment => {
    setState(prevState => ({
      ...prevState,
      loadingButton: true
    }));
    createComment(cids, section, comment, token)
      .then(createdComment => {
        setState(prevState => ({
          ...prevState,
          commentData: createdComment,
          editMode: false,
          loadingButton: false
        }));
        onChange(createdComment);
      })
      .catch(catchFunction);
  };

  const handlePromptClose = commentId => {
    if (commentId) {
      setState(prevState => ({
        ...prevState,
        loading: true
      }));
      deleteComment(commentId, token)
        .then(() => {
          setState(prevState => ({
            ...prevState,
            commentData: null,
            editMode: true,
            loading: false,
            unsavedComment: undefined
          }));
          onChange(null);
        })
        .catch(catchFunction);
    }
    setState(prevState => ({
      ...prevState,
      commentIdToDelete: undefined,
      showPrompt: false
    }));
  };

  const handleEditButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      editMode: !editMode,
      ...(!editMode && commentData && { unsavedComment: commentData.comment })
    }));
  };

  const handleDeleteButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      commentIdToDelete: commentData._id,
      showPrompt: true
    }));
  };

  const handleSaveButtonClick = () => {
    if (!unsavedComment && commentData) {
      setState(prevState => ({
        ...prevState,
        commentIdToDelete: commentData._id,
        showPrompt: true
      }));
    } else if (commentData) {
      performUpdateComment(commentData._id, unsavedComment);
    } else {
      performCreateComment(unsavedComment);
    }
  };

  const handleResetButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      unsavedComment: ''
    }));
  };

  const handleCancelButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      editMode: !commentData,
      unsavedComment: undefined
    }));
    onCancel?.();
  };

  const handleTextAreaChange = text => {
    setState(prevState => ({
      ...prevState,
      unsavedComment: text
    }));
  };

  useDidUpdate(() => {
    if (onlyView) {
      setState(prevState => ({
        ...prevState,
        editMode: !onlyView
      }));
    }
  }, [onlyView]);

  useEffect(() => {
    loadComments();
    return () => token.cancel?.();
  }, []);

  if (hide) return null;

  return (
    <ReportingCommentSectionStyle className="commentSection">
      <BoxV2 className="commentSection__box" id="commentSection-box">
        <BoxV2.Body padding="16px">
          <div className="commentSection__mainContent">
            {loading ? (
              <LoadingSkeleton count={1} height={140} />
            ) : editMode ? (
              <TextArea
                autoFocus
                className="commentSection__commentTextArea"
                disabled={loadingButton}
                id={`commentSection-commentTextArea-${section}`}
                maxLength={COMMENT_MAX_LENGTH}
                onChange={handleTextAreaChange}
                placeholder={formatMessage({
                  defaultMessage: 'Write a comment you would like to share...',
                  id: 'reporting.commentSection.textAreaPlaceholder'
                })}
                showLengthCounter
                value={unsavedComment != null ? unsavedComment : commentData?.comment}
              />
            ) : (
              <BoxV2 borderColor="tableBorderRow" id="commentSection-box-text">
                <BoxV2.Body className="commentSection__saveComment" padding="16px">
                  {commentData?.comment}
                </BoxV2.Body>
              </BoxV2>
            )}
          </div>
          {!onlyView && (
            <div className="commentSection__buttonsArea">
              <div className="commentSection__buttonsAreaLeftSide">
                {(commentData?._id || unsavedComment) && onCancel && editMode && (
                  <ButtonUI
                    className="commentSection__cancelButton"
                    disabled={loading}
                    onClick={handleCancelButtonClick}
                    shape="link"
                    size="small"
                    text={formatMessage({
                      defaultMessage: 'Cancel changes',
                      id: 'reporting.commentSection.cancelButton'
                    })}
                    type="secondary"
                  />
                )}
              </div>
              {editMode ? (
                <div className="commentSection__buttonsAreaRightSide">
                  <ButtonUI
                    disabled={!unsavedComment || loading}
                    icon="far fa-trash"
                    onClick={handleResetButtonClick}
                    shape="squareRounded"
                    size="small"
                    text={formatMessage({
                      defaultMessage: 'Clear comment',
                      id: 'reporting.commentSection.clearButton'
                    })}
                    type="secondary"
                  />
                  <ButtonUI
                    disabled={!unsavedComment && !commentData}
                    icon="far fa-save"
                    loading={loadingButton}
                    onClick={handleSaveButtonClick}
                    shape="squareRounded"
                    size="small"
                    text={formatMessage({
                      defaultMessage: 'Save',
                      id: 'common.save'
                    })}
                    type="primary"
                  />
                </div>
              ) : (
                <div className="commentSection__buttonsAreaRightSide">
                  <ButtonUI
                    disabled={loading}
                    icon="far fa-trash"
                    onClick={handleDeleteButtonClick}
                    shape="squareRounded"
                    size="small"
                    text={formatMessage({
                      defaultMessage: 'Delete',
                      id: 'common.delete'
                    })}
                    type="secondary"
                  />
                  <ButtonUI
                    disabled={loading}
                    icon="far fa-edit"
                    onClick={handleEditButtonClick}
                    shape="squareRounded"
                    size="small"
                    text={formatMessage({
                      defaultMessage: 'Edit',
                      id: 'common.edit'
                    })}
                    type="primary"
                  />
                </div>
              )}
            </div>
          )}
          {showPrompt && commentIdToDelete && (
            <ReportingCommentDeletionPrompt commentId={commentIdToDelete} onClose={handlePromptClose} />
          )}
        </BoxV2.Body>
      </BoxV2>
    </ReportingCommentSectionStyle>
  );
}

ReportingCommentSection.propTypes = {
  hide: PropTypes.bool,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  onlyView: PropTypes.bool,
  section: PropTypes.oneOf([
    'campaignsProgress',
    'footfallAttributionInsights',
    'general',
    'insights',
    'maps',
    'summary',
    'table'
  ]).isRequired
};

export default ReportingCommentSection;
